import React, { useEffect, useRef, useState } from "react";

const radius = 78;
const stroke = 10;
const normalizedRadius = radius - stroke * 0.5;
const circumference = 2 * Math.PI * normalizedRadius;

export default function AccuracyGauge({ value = 0, checklist = [] }) {
  const clampedValue = Math.max(0, Math.min(95, value));
  const [displayValue, setDisplayValue] = useState(100);
  const animationRef = useRef(null);
  const hasBootAnimated = useRef(false);
  const displayValueRef = useRef(100);
  const hasStartedRef = useRef(false);

  useEffect(() => {
    const animateTo = (startValue, endValue, durationMs) => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      const startTime = performance.now();
      const tick = (time) => {
        const progress = Math.min(1, (time - startTime) / durationMs);
        const nextValue =
          startValue + (endValue - startValue) * progress;
        const clampedNext = Math.max(0, Math.min(100, nextValue));
        displayValueRef.current = clampedNext;
        setDisplayValue(clampedNext);
        if (progress < 1) {
          animationRef.current = requestAnimationFrame(tick);
        }
      };
      animationRef.current = requestAnimationFrame(tick);
    };

    if (!hasStartedRef.current) {
      hasStartedRef.current = true;
      displayValueRef.current = 100;
      setDisplayValue(100);
    }

    const runAnimation = (isFirst) => {
      const startValue = isFirst ? 100 : displayValueRef.current;
      const durationMs = isFirst ? 2800 : 700;
      hasBootAnimated.current = true;
      if (isFirst) {
        displayValueRef.current = 100;
        setDisplayValue(100);
      }
      animateTo(startValue, clampedValue, durationMs);
    };

    const bootLoader = document.getElementById("boot-loader");
    const bootCompleted =
      !bootLoader ||
      bootLoader.classList.contains("is-hidden") ||
      !document.body.classList.contains("boot-loading");

    if (bootCompleted) {
      const isFirst = !hasBootAnimated.current;
      requestAnimationFrame(() => runAnimation(isFirst));
    } else {
      const onBootDone = () => {
        const isFirst = !hasBootAnimated.current;
        requestAnimationFrame(() => runAnimation(isFirst));
      };
      window.addEventListener("bootloader:done", onBootDone, { once: true });
      return () => {
        window.removeEventListener("bootloader:done", onBootDone);
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
      };
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [clampedValue]);
  const visualValue = Math.max(0, Math.min(100, displayValue));
  const offset =
    circumference - (visualValue / 100) * circumference;

  return (
    <div className="glass-card p-6 md:p-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[var(--muted)]">
            Accuracy Estimation
          </p>
          <h2 className="mt-2 text-2xl font-display text-[var(--ink)]">
            Confidence Meter
          </h2>
          <p className="mt-2 text-sm text-[var(--muted)]">
            Weekly detail leads to a sharper, less generic estimate.
          </p>
        </div>
        <span
          className="rounded-full bg-white/60 px-3 py-1 text-[11px] uppercase tracking-widest text-[var(--muted)]"
          title="Heuristic confidence, not a medical guarantee."
        >
          Heuristic
        </span>
      </div>

      <div className="mt-6 flex flex-col gap-6 md:flex-row md:items-center">
        <div className="relative flex items-center justify-center">
          <svg width="190" height="190">
            <defs>
              <linearGradient
                id="accuracyGradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop offset="0%" stopColor="var(--accent-2)" />
                <stop offset="100%" stopColor="var(--accent)" />
              </linearGradient>
            </defs>
            <circle
              cx="95"
              cy="95"
              r={normalizedRadius}
              fill="none"
              stroke="rgba(255,255,255,0.7)"
              strokeWidth={stroke}
            />
            <circle
              cx="95"
              cy="95"
              r={normalizedRadius}
              fill="none"
              stroke="url(#accuracyGradient)"
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-4xl font-display text-[var(--ink)]">
              {Math.round(visualValue)}%
            </span>
            <span className="text-xs uppercase tracking-[0.2em] text-[var(--muted)]">
              Confidence
            </span>
          </div>
        </div>

        <div className="flex-1">
          <p className="text-xs uppercase tracking-[0.3em] text-[var(--muted)]">
            What increases accuracy
          </p>
          <ul className="mt-3 grid gap-2 text-xs text-[var(--muted)]">
            {checklist.map((item) => (
              <li
                key={item.label}
                className="flex items-center gap-2"
              >
                <span
                  className={`h-2.5 w-2.5 rounded-full border ${
                    item.done
                      ? "border-transparent bg-[var(--accent-2)]"
                      : "border-white/50 bg-transparent"
                  }`}
                />
                <span
                  className={
                    item.done ? "text-[var(--ink)]" : ""
                  }
                >
                  {item.label}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-xs text-[var(--muted)]">
            Confidence is capped at 95% in the free version.
          </p>
        </div>
      </div>
    </div>
  );
}
